import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { LibItem, UserShortInfo } from "../../types";
import ManShortSelector from "../../components/ui/shortItemSelect";
import { libBackend, backend, GroupData, SourceData } from "../../services";
import { RootState } from "../../state/reducer";
import { toast } from "sonner";
import MultiCheckbox from "../../components/ui/multiCheckbox";
import { statusNames } from "../../constants/events";
import Calendar from "icons/calendar";
import DateInput from "../../components/ui/dateInput";
import { SearchIcon } from "../../icons";
import {
  clientFiltersCacheKey,
  CustomerListFilters,
  defaultCustomerFilters,
} from "../../forms/filters/customers"; // Импортируем новый интерфейс и константы
import { currentUserGroupFilter } from "../../state/functions";
import { hasCommonElement } from "../../utils";

interface CustomerFilterModalProps {
  onApplyFilters: (filters: CustomerListFilters) => void;
  closeModal: () => void;
}

const CustomerFilterModal: React.FC<CustomerFilterModalProps> = ({
  onApplyFilters,
  closeModal,
}) => {
  const State = useSelector((state: RootState) => state);
  const [usersToSelect, setUsersToSelect] = useState<UserShortInfo[]>([]);
  const groupList = State.groupsToSelect;
  const [sourceList, setSourceList] = useState<SourceData[]>([]);
  const [filters, setFilters] = useState<CustomerListFilters>(
    defaultCustomerFilters
  );
  const rights = State.rights;
  const session = State.session;
  useEffect(() => {
    const cachedFilters = localStorage.getItem(clientFiltersCacheKey);
    if (cachedFilters) {
      const lastFilters: CustomerListFilters = JSON.parse(cachedFilters);
      setFilters(lastFilters);
    } else {
      setFilters(State.customerFilters);
    }
  }, [groupList]);

  useEffect(() => {
    backend
      .getAllUsersShorts(
        filters.group_id
          ? { group_id: filters.group_id }
          : currentUserGroupFilter()
      )
      .then((res) => {
        setUsersToSelect(res.users);
      });

    backend.getCustomerSources().then((res) => {
      setSourceList(res.sources);
    });
  }, [filters.group_id]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleParamChange = (name: keyof CustomerListFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    const dataToCache = JSON.stringify(filters);
    localStorage.setItem(clientFiltersCacheKey, dataToCache);
    onApplyFilters(filters);
    closeModal();
  };

  console.log("Selected group:", filters.group_id);

  const availableUsersToSearch =
    rights.rang < 3 || hasCommonElement(rights.permissions, [10, 11])
      ? [{ id: -1, name: "Свободные" }, ...usersToSelect]
      : [
          { id: -1, name: "Свободные" },
          { id: session?.id, name: "Мои" },
        ];

  return (
    <div className="creationForm filterForm">
      <div className="modalHeading">
        <h3>Поиск по клиентам</h3>
      </div>
      <form className="columnCenter">
        <div className="flex formRow">
          <div className="fieldName">Ответственный</div>
          <div className="fieldInput flex">
            <ManShortSelector
              items={availableUsersToSearch}
              defaultSelectedId={0}
              enabledAll={true}
              onChangeCallback={(id) =>
                setFilters((prev) => ({ ...prev, user_id: id }))
              }
            />
          </div>
        </div>
        {(rights.rang < 3 ||
          hasCommonElement(rights.permissions, [10, 11])) && (
          <>
            <div className="flex formRow">
              <div className="fieldName">Группа</div>
              <div className="fieldInput flex">
                <select
                  id="group_id"
                  name="group_id"
                  value={filters.group_id || 0}
                  onChange={(event) => {
                    const group = Number(event.target.value);
                    console.log("Selected:", group);
                    setFilters((prev) => ({
                      ...prev,
                      group_id: group,
                    }));
                  }}
                >
                  {rights.rang < 3 && <option value="0">Любая</option>}
                  {groupList.map((item, index) => {
                    return (
                      <option key={index} value={item.id}>
                        {item.name}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <div className="flex formRow">
              <div className="fieldName flex gap12">
                <input
                  className="checkBoxInput"
                  type="checkbox"
                  checked={filters.search_in_subgroup || false}
                  onChange={(e) =>
                    handleParamChange("search_in_subgroup", e.target.checked)
                  }
                />
                <span>Искать в подгруппах</span>
              </div>
            </div>
          </>
        )}
        <div className="flex formRow">
          <div className="fieldName">Источник</div>
          <div className="fieldInput flex">
            <select
              id="source_id"
              name="source_id"
              value={filters.source_id || 0}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  source_id: Number(event.target.value),
                }))
              }
            >
              <option value="0">Все</option>
              {sourceList.map((item, index) => {
                return (
                  <option key={index} value={item.id}>
                    {item.name}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        <div className="flex formRow">
          <div className="fieldName">Дата создания</div>
          <div className="fieldInput flex between dateFromTo gap24">
            <DateInput
              value={filters.date_created_min || ""}
              onSelect={(dateTime) =>
                handleParamChange("date_created_min", dateTime)
              }
            />
            <DateInput
              value={filters.date_created_max || ""}
              onSelect={(dateTime) =>
                handleParamChange("date_created_max", dateTime)
              }
            />
          </div>
        </div>
        <div className="flex formRow">
          <div className="fieldName">Направление сортировки</div>
          <div className="fieldInput flex">
            <select
              id="order_dir"
              name="order_dir"
              value={filters.order_dir}
              onChange={handleInputChange}
            >
              <option value="ASC">Сначала старые</option>
              <option value="DESC">Сначала новые</option>
            </select>
          </div>
        </div>

        {/* Кнопки действий */}
        <div className="flex btnRow">
          <button className="filterButton" type="button" onClick={applyFilters}>
            Применить
          </button>
          <button className="cancelButton" type="button" onClick={closeModal}>
            Отменить
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerFilterModal;
